/**
 * Thin API call helper used by feature-specific API modules.
 *
 * Wraps apiSdk for authenticated requests and falls back to plain fetch for
 * public (skipAuth) endpoints. Returns the standardised ApiResponse envelope
 * so callers receive { success, data?, error? } rather than raw throws.
 */

import { APP_CONFIG } from '@/config/app.config';
import { apiSdk, isApiClientError } from './sdk';

export interface ApiCallInit extends Omit<RequestInit, 'headers'> {
  /** When true the Authorization header is omitted (public endpoints). */
  skipAuth?: boolean;
  headers?: Record<string, string>;
}

export interface ApiResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  /** HTTP status of the failed response; undefined for network errors. */
  status?: number;
}

/**
 * Make an API request and return an ApiResult envelope.
 *
 * - Authenticated requests (skipAuth omitted / false) go through apiSdk so
 *   token refresh, 401 retry, and session-expiry handling are all applied.
 * - Public requests (skipAuth: true) bypass auth entirely.
 */
export async function apiCall<T = unknown>(
  endpoint: string,
  init: ApiCallInit = {},
): Promise<ApiResult<T>> {
  const { skipAuth, ...fetchInit } = init;

  if (skipAuth) {
    const url = endpoint.startsWith('http')
      ? endpoint
      : `${APP_CONFIG.API_BASE_URL}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...fetchInit,
        headers: {
          'Content-Type': 'application/json',
          ...(fetchInit.headers ?? {}),
        },
      });
      const json = await response.json();
      if (!response.ok || json.success === false) {
        const rawError = (json as { error?: string | { message?: string } }).error;
        return {
          success: false,
          error:
            typeof rawError === 'string'
              ? rawError
              : rawError?.message ?? `API error: ${response.status}`,
          status: response.status,
        };
      }
      const data =
        json && typeof json === 'object' && 'data' in json
          ? (json as { data: T }).data
          : (json as T);
      return { success: true, data };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Network error',
      };
    }
  }

  try {
    const data = await apiSdk.request<T>(endpoint, fetchInit);
    return { success: true, data };
  } catch (error) {
    if (isApiClientError(error)) {
      return { success: false, error: error.message };
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}
